'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './QRCaseStudy.module.css';
import type { Locale } from '@/lib/i18n';
import { getIntegrationOrPartnerPath } from '@/lib/routes';

interface Integration {
  name: string;
  logo: string;
  category: string;
}

interface QRCaseStudyItem {
  company: string;
  solution: string;
  location: string;
  images?: string[];
  videoWebm?: string;
  videoMp4?: string;
  description: string;
  integrations: Integration[];
}

export interface QRCaseStudyProps {
  badge: string;
  title: string;
  subtitle: string;
  cases: QRCaseStudyItem[];
  labels: {
    client: string;
    solution: string;
    location: string;
    integratedWith: string;
  };
  altTexts?: Record<string, string>;
  locale?: Locale;
}

/**
 * QRCaseStudy Component
 * Displays QR ordering case studies with image carousel
 */
export function QRCaseStudy({ badge, title, subtitle, cases, labels, altTexts, locale = 'en' }: QRCaseStudyProps) {
  const [activeImageIndices, setActiveImageIndices] = useState<Record<number, number>>({});
  const [isVisible, setIsVisible] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Auto-rotate images every 3.5 seconds for each case
  useEffect(() => {
    const intervals: NodeJS.Timeout[] = [];

    cases.forEach((caseItem, caseIndex) => {
      if (caseItem.images && caseItem.images.length > 1) {
        const interval = setInterval(() => {
          setActiveImageIndices((prev) => ({
            ...prev,
            [caseIndex]: ((prev[caseIndex] || 0) + 1) % caseItem.images!.length,
          }));
        }, 3500);
        intervals.push(interval);
      }
    });

    return () => {
      intervals.forEach((interval) => clearInterval(interval));
    };
  }, [cases]);

  // Lazy loading observer for images and videos
  useEffect(() => {
    if (!isVisible) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setIsVisible(true);
          }
        },
        { rootMargin: '300px' }  // Load section early for smooth experience
      );

      const element = document.getElementById('qr-case-study');
      if (element) {
        observer.observe(element);
      }

      return () => {
        if (element) {
          observer.unobserve(element);
        }
      };
    }
  }, [isVisible]);

  // Lazy load videos when visible
  // ✅ PERFORMANCE: Balanced rootMargin for smooth UX
  useEffect(() => {
    if (!isVisible) return;

    // Detect mobile - larger margin for smoother experience
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const rootMargin = isMobileDevice ? '200px' : '300px';

    const observers = videoRefs.current.map((video) => {
      if (!video) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const videoElement = entry.target as HTMLVideoElement;
              const sources = videoElement.querySelectorAll('source');
              sources.forEach((source) => {
                if (!source.src && source.dataset.src) {
                  source.src = source.dataset.src;
                }
              });
              videoElement.load();
              observer.unobserve(videoElement);
            }
          });
        },
        { rootMargin }  // 200px mobile, 300px desktop - smoother UX
      );

      observer.observe(video);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, [isVisible]);

  return (
    <section id="qr-case-study" className={styles.caseStudy}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.badge}>{badge}</div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        {cases.map((caseItem, caseIndex) => (
          <div key={caseIndex} className={`${styles.caseItem} ${caseIndex % 2 === 1 ? styles.reversed : ''}`}>
            <div className={styles.textContent}>
              <div className={styles.company}>
                <span className={styles.companyLabel}>{labels.client}</span>
                <span className={styles.companyName}>{caseItem.company}</span>
              </div>
              <div className={styles.solution}>
                <span className={styles.solutionLabel}>{labels.solution}</span>
                <span className={styles.solutionName}>{caseItem.solution}</span>
              </div>
              
              <div className={styles.descriptionWrapper}>
                <p className={styles.description}>{caseItem.description}</p>
                
                {caseItem.integrations && caseItem.integrations.length > 0 && (
                  <div className={styles.integrations}>
                    <h3 className={styles.integrationsTitle}>{labels.integratedWith}</h3>
                    <div className={styles.integrationLogos}>
                      {caseItem.integrations.map((integration, idx) => {
                        // Generate integration slug for URL
                        const slug = integration.name.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '');
                        
                        // Use centralized route function
                        const integrationUrl = getIntegrationOrPartnerPath(slug, locale);
                        
                          return (
                          <Link key={idx} href={integrationUrl} className={styles.integrationLogo} prefetch={true}>
                            <Image
                              src={integration.logo}
                              alt={altTexts?.[integration.name.toLowerCase().replace(/\s+/g, '-')] || `${integration.name} integration`}
                              width={120}
                              height={60}
                              style={{ objectFit: 'contain' }}
                            />
                            <span className={styles.integrationName}>{integration.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.imageContainer}>
              {isVisible && caseItem.images && caseItem.images.length > 0 ? (
                <div className={styles.phoneCarousel}>
                  {caseItem.images.map((imageSrc, imgIndex) => (
                    <div
                      key={imgIndex}
                      className={`${styles.phoneWrapper} ${
                        (activeImageIndices[caseIndex] || 0) === imgIndex ? styles.active : ''
                      }`}
                    >
                      <div className={styles.phoneFrame}>
                        <Image
                          src={imageSrc}
                          alt={`${caseItem.company} QR ordering screenshot ${imgIndex + 1}`}
                          width={400}
                          height={825}
                          quality={90}
                          priority={caseIndex === 0 && imgIndex === 0}
                          loading={caseIndex === 0 && imgIndex === 0 ? 'eager' : 'lazy'}
                          className={styles.phoneImage}
                        />
                      </div>
                    </div>
                  ))}
                  
                  {/* Carousel dots */}
                  {caseItem.images.length > 1 && (
                    <div className={styles.carouselDots}>
                      {caseItem.images.map((_, idx) => (
                        <button
                          key={idx}
                          className={`${styles.dot} ${(activeImageIndices[caseIndex] || 0) === idx ? styles.activeDot : ''}`}
                          onClick={() => setActiveImageIndices((prev) => ({ ...prev, [caseIndex]: idx }))}
                          aria-label={`View screenshot ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : isVisible && caseItem.videoWebm && caseItem.videoMp4 ? (
                <video
                  ref={(el) => { videoRefs.current[caseIndex] = el; }}
                  className={styles.video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="none"
                  poster={`/videos/posters/${caseItem.videoWebm.split('/').pop()?.replace('.webm', '')}.webp`}
                  aria-label={`${caseItem.company} kiosk in action`}
                >
                  <source
                    data-src={`/videos/${caseItem.videoMp4.split('/').pop()?.replace('.mp4', '')}-540p.mp4`}
                    type="video/mp4"
                    media="(max-width: 1024px)"
                  />
                  <source
                    data-src={`/videos/${caseItem.videoMp4.split('/').pop()?.replace('.mp4', '')}-720p.mp4`}
                    type="video/mp4"
                    media="(min-width: 1025px)"
                  />
                  <source
                    data-src={`/videos/${caseItem.videoWebm.split('/').pop()?.replace('.webm', '')}-540p.webm`}
                    type="video/webm"
                    media="(max-width: 1024px)"
                  />
                  <source
                    data-src={`/videos/${caseItem.videoWebm.split('/').pop()?.replace('.webm', '')}-720p.webm`}
                    type="video/webm"
                    media="(min-width: 1025px)"
                  />
                  Your browser does not support video.
                </video>
              ) : null}
              <div className={styles.imageGlow} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

